<template>
  <template v-if="hasRequiredRole">
    <slot />
  </template>
  <template v-else-if="$slots.fallback">
    <slot name="fallback" />
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useUserRoles } from '@/modules/admin/composables/useUserRoles';
import type { UserRole } from '@/modules/admin/interfaces/UserRoles';

/**
 * Renders content only when the current user has one of the given roles.
 * Use the default slot for role-restricted content and the optional "fallback"
 * slot for content shown when the user does not have any of the roles.
 *
 * @example
 * <!-- Show username only for Admin/Supervisor, show "Judge" for others -->
 * <RoleBasedContent :roles="[UserRoles.ADMIN, UserRoles.SUPERVISOR]">
 *   <TextWithTooltip :text="verdict.user?.username" />
 *   <template #fallback>
 *     <span class="text-gray">Judge</span>
 *   </template>
 * </RoleBasedContent>
 *
 * @example
 * <!-- Show edit button only for Admin -->
 * <RoleBasedContent :roles="[UserRoles.ADMIN]">
 *   <a-button>Edit</a-button>
 * </RoleBasedContent>
 */
const props = withDefaults(
  defineProps<{
    /** Role name or list of role names. Content is visible when user has any of these roles. */
    roles: UserRole | readonly UserRole[];
  }>(),
  {}
);

const { userRole } = useUserRoles();

const rolesList = computed(() =>
  Array.isArray(props.roles) ? props.roles : [props.roles]
);

const hasRequiredRole = computed(() =>
  userRole.value ? rolesList.value.includes(userRole.value) : false
);
</script>
